<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { NSpin } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import LogEntryRow from './LogEntryRow.vue'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { useSessionStore } from '../stores/session'
import { useDocumentTitle } from '../composables/useDocumentTitle'
import { LOG_PAGE_PATH } from '../lib/navConfig'
import {
  LOG_KINDS,
  countLogKinds,
  firstSeenEntryId,
  groupLogByDay,
  parseBrainLog,
  type LogKind,
} from '../composables/parseBrainLog'

// The brain's activity log at `/log`: `wiki/log.md` parsed into entries,
// grouped by day, filterable by operation, with a "Since your last visit"
// divider. The data is the wiki page itself (found in the catalog, then
// fetched like HomeView fetches any page) — there is no log API.
//
// The divider uses the `last_seen_log_at` the reader arrived with: the
// nav's Log entry marks the log seen as it navigates here, and that
// round-trip lands after this view has taken its snapshot. "Mark all
// read" marks it seen again and moves the divider to the top.

const PAGE_SIZE = 200

const wikiPages = useWikiPagesStore()
const { pages, loaded } = storeToRefs(wikiPages)
const session = useSessionStore()

useDocumentTitle('Log')

const content = ref<string | null>(null)
const loading = ref(true)
const loadError = ref<string | null>(null)
const marking = ref(false)
const seenAt = ref<string | null>(session.currentUser?.last_seen_log_at ?? null)
const kindFilter = ref<LogKind | null>(null)
const limit = ref(PAGE_SIZE)

const logPage = computed(() => pages.value.find((p) => p.path === LOG_PAGE_PATH) ?? null)
const knownPaths = computed(() => new Set(pages.value.map((p) => p.path)))

async function loadLog(): Promise<void> {
  loadError.value = null
  try {
    if (!loaded.value) await wikiPages.fetchAll()
    const page = logPage.value
    content.value = page ? ((await wikiPages.fetchContent(page.id))?.content ?? null) : null
  } catch (e) {
    loadError.value = e instanceof Error ? e.message : 'Failed to load the log'
  } finally {
    loading.value = false
  }
}

onMounted(loadLog)
// A live catalog update (new ingest, nightly lint) bumps log.md's
// updated_at — refetch so the page follows along.
watch(
  () => logPage.value?.updated_at,
  (next, prev) => {
    if (prev && next && next !== prev) void loadLog()
  },
)

const entries = computed(() => (content.value ? parseBrainLog(content.value) : []))
const kindCounts = computed(() => countLogKinds(entries.value))
const filterChips = computed(() =>
  (Object.keys(LOG_KINDS) as LogKind[])
    .filter((kind) => kindCounts.value[kind])
    .map((kind) => ({ kind, label: LOG_KINDS[kind].label, count: kindCounts.value[kind] ?? 0 })),
)
const filtered = computed(() =>
  kindFilter.value ? entries.value.filter((e) => e.kind === kindFilter.value) : entries.value,
)
const shown = computed(() => filtered.value.slice(0, limit.value))
const days = computed(() => groupLogByDay(shown.value, new Date()))
const dividerBefore = computed(() => firstSeenEntryId(shown.value, seenAt.value))

function selectKind(kind: LogKind | null): void {
  kindFilter.value = kind
  limit.value = PAGE_SIZE
}

async function markAllRead(): Promise<void> {
  marking.value = true
  try {
    await session.markLogSeen()
    seenAt.value = session.currentUser?.last_seen_log_at ?? seenAt.value
  } finally {
    marking.value = false
  }
}

const CHIP =
  'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] transition-colors max-sm:min-h-11'
const CHIP_ON = 'border-brand-primary bg-brand-primary/10 text-ink'
const CHIP_OFF = 'border-border-control text-ink-muted hover:text-ink'
</script>

<template>
  <RouteShell>
    <section class="flex max-w-[760px] flex-col gap-[18px] pt-2 pb-8" data-testid="log-view">
      <header class="flex flex-wrap items-end gap-3">
        <div class="min-w-[220px] flex-1">
          <h1 class="page-title">Log</h1>
          <p class="m-0 mt-1.5 text-ink-muted">
            Everything the brain did, newest first. Mirrors <span class="font-mono text-[13px]">wiki/log.md</span>.
          </p>
        </div>
        <button
          type="button"
          data-testid="log-mark-read"
          class="min-h-10 rounded-[10px] border border-border-control px-4 text-ink transition-colors hover:bg-surface-hover disabled:opacity-60 max-sm:min-h-11"
          :disabled="marking"
          @click="markAllRead"
        >
          Mark all read
        </button>
      </header>

      <div v-if="loading" class="flex justify-center py-12"><NSpin /></div>

      <p v-else-if="loadError" class="m-0 text-sm text-status-alert" data-testid="log-error">
        {{ loadError }}
        <button type="button" class="ml-2 min-h-8 text-brand-primary underline" @click="loadLog">Retry</button>
      </p>

      <p v-else-if="entries.length === 0" class="m-0 text-ink-muted" data-testid="log-empty">
        Nothing logged yet. Ingests, lint runs and edits will show up here.
      </p>

      <template v-else>
        <div role="group" aria-label="Filter by operation" class="flex flex-wrap gap-1.5">
          <button
            type="button"
            data-testid="log-filter-chip"
            :aria-pressed="kindFilter === null"
            :class="[CHIP, kindFilter === null ? CHIP_ON : CHIP_OFF]"
            @click="selectKind(null)"
          >
            All <span class="text-ink-faint">{{ entries.length }}</span>
          </button>
          <button
            v-for="chip in filterChips"
            :key="chip.kind"
            type="button"
            data-testid="log-filter-chip"
            :aria-pressed="kindFilter === chip.kind"
            :class="[CHIP, kindFilter === chip.kind ? CHIP_ON : CHIP_OFF]"
            @click="selectKind(chip.kind)"
          >
            {{ chip.label }} <span class="text-ink-faint">{{ chip.count }}</span>
          </button>
        </div>

        <section v-for="day in days" :key="day.key" class="flex flex-col" data-testid="log-day">
          <h2 class="m-0 mt-2.5 mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-ink-faint">
            {{ day.label }}
          </h2>
          <template v-for="entry in day.entries" :key="entry.id">
            <div
              v-if="entry.id === dividerBefore"
              role="separator"
              aria-label="Entries above are new since your last visit"
              class="my-1.5 flex items-center gap-2.5 text-xs font-semibold text-attention"
              data-testid="log-since-last-visit"
            >
              <span class="h-px flex-1 bg-attention/50" />Since your last visit<span
                class="h-px flex-1 bg-attention/50"
              />
            </div>
            <LogEntryRow :entry="entry" :known-paths="knownPaths" />
          </template>
        </section>

        <button
          v-if="filtered.length > shown.length"
          type="button"
          data-testid="log-show-older"
          class="min-h-10 self-start rounded-[10px] border border-border-control px-4 text-sm text-ink hover:bg-surface-hover max-sm:min-h-11"
          @click="limit += PAGE_SIZE"
        >
          Show older ({{ filtered.length - shown.length }} more)
        </button>
      </template>
    </section>
  </RouteShell>
</template>
